// SCR-STAFF-807 (AP-CMP-02؛ US-CMP-060 تا 062؛ AC-CMP-060.1..2، 061.1، 062.1): پرتال ارائه‌دهنده — نمای موبایل‌محور در apps/staff برای کاربر با
// cmp.share.view_own: کارکرد امروز، ماه جاری و ماه قبل، اقلام سهم خود، صورت‌وضعیت‌ها با پذیرش یا اعتراض قلم‌به‌قلم و نوبت‌های پیش‌رو.
// فقط داده خود کاربر (هویت از عضویت — C-AGENT-1386)؛ نام مراجعه‌کننده فقط نام نمایشی، بدون داده بالینی.
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { unwrap, type CmpPortalPeriodView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  EmptyState,
  ErrorState,
  faDigits,
  JalaliDate,
  ListPage,
  LoadingState,
  Money,
  StatusChip,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { compensationKeys, lineTone, LIST_SEPARATOR, statementTone } from './common';
import { StatementDetail } from './StatementDetail';

export const SCR_STAFF_807 = 'SCR-STAFF-807';

type PortalTab = 'entries' | 'statements' | 'appointments';

function PeriodCard({
  titleKey,
  period,
  testId,
}: {
  titleKey: string;
  period: CmpPortalPeriodView;
  testId: string;
}) {
  const { t } = useTranslation('cmp');
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }} data-testid={testId}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {t(titleKey)}
        </Typography>
        <Typography variant="h6" data-testid={`${testId}-earned`}>
          <Money amountMinor={Number(period.earned.amount)} />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('cmp:portal.services', {
            count: Number(period.services),
            value: faDigits(Number(period.services)),
          })}
        </Typography>
        {Number(period.pendingCollection.amount) > 0 && (
          <Typography variant="body2" color="warning.main">
            {t('cmp:portal.pending_collection')}:{' '}
            <Money amountMinor={Number(period.pendingCollection.amount)} />
          </Typography>
        )}
        {Number(period.commissions.amount) > 0 && (
          <Typography variant="body2">
            {t('cmp:portal.commissions')}: <Money amountMinor={Number(period.commissions.amount)} />
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function ProviderPortalPage() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [tab, setTab] = useState<PortalTab>('entries');
  const summary = useQuery({
    queryKey: [...compensationKeys.portal(session?.tenantId), 'summary'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/me/summary')),
  });

  return (
    <ListPage screenId={SCR_STAFF_807} titleKey="cmp:portal.title" subtitleKey="cmp:portal.subtitle">
      {summary.isPending ? (
        <LoadingState />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <Stack spacing={2}>
          {!summary.data.providerId && <EmptyState titleKey="cmp:portal.not_provider" />}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <PeriodCard titleKey="cmp:portal.today" period={summary.data.today} testId="portal-today" />
            <PeriodCard titleKey="cmp:portal.month" period={summary.data.month} testId="portal-month" />
            <PeriodCard
              titleKey="cmp:portal.previous_month"
              period={summary.data.previousMonth}
              testId="portal-previous"
            />
          </Stack>
          <Tabs value={tab} onChange={(_, value: PortalTab) => setTab(value)} variant="fullWidth">
            <Tab value="entries" label={t('cmp:portal.tab_entries')} data-testid="portal-tab-entries" />
            <Tab
              value="statements"
              label={t('cmp:portal.tab_statements')}
              data-testid="portal-tab-statements"
            />
            <Tab
              value="appointments"
              label={t('cmp:portal.tab_appointments')}
              data-testid="portal-tab-appointments"
            />
          </Tabs>
          {tab === 'entries' && <EntriesList />}
          {tab === 'statements' && <StatementsList />}
          {tab === 'appointments' && <AppointmentsList />}
        </Stack>
      )}
    </ListPage>
  );
}

function EntriesList() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const entries = useQuery({
    queryKey: [...compensationKeys.portal(session?.tenantId), 'entries'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/me/entries', { params: { query: {} } })),
  });
  if (entries.isPending) {
    return <LoadingState />;
  }
  if (entries.error) {
    return <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />;
  }
  if (entries.data.items.length === 0) {
    return <EmptyState titleKey="cmp:portal.no_entries" />;
  }
  return (
    <List dense aria-label={t('cmp:portal.tab_entries')} data-testid="portal-entries">
      {entries.data.items.map((row) => (
        <ListItem key={row.id} divider disableGutters sx={{ px: 1 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <span>{row.serviceName ?? t(`cmp:entry_kind.${row.kind}`)}</span>
                <Money amountMinor={Number(row.amount.amount)} />
              </Stack>
            }
            secondary={
              <Stack direction="row" spacing={1} alignItems="center" component="span">
                <JalaliDate value={row.businessDate} />
                {row.patientName && <span>{row.patientName}</span>}
                {row.status !== 'Earned' && (
                  <StatusChip tone={lineTone(row.status)} labelKey={`cmp:line_status.${row.status}`} />
                )}
              </Stack>
            }
            slotProps={{ secondary: { component: 'div' } }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function StatementsList() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [openId, setOpenId] = useState<string | null>(null);
  const statements = useQuery({
    queryKey: [...compensationKeys.portal(session?.tenantId), 'statements'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/me/statements')),
  });
  if (statements.isPending) {
    return <LoadingState />;
  }
  if (statements.error) {
    return <ErrorState error={statements.error} onRetry={() => void statements.refetch()} />;
  }
  if (statements.data.items.length === 0) {
    return <EmptyState titleKey="cmp:portal.no_statements" />;
  }
  return (
    <>
      <List aria-label={t('cmp:portal.tab_statements')}>
        {statements.data.items.map((row) => (
          // ListItemButton یک div است؛ فرزند مستقیم <ul> باید <li> باشد (axe: list).
          <ListItem key={row.id} disablePadding divider>
            <ListItemButton onClick={() => setOpenId(row.id)} data-testid={`portal-statement-${row.id}`}>
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <span>
                      <JalaliDate value={row.periodFrom} /> – <JalaliDate value={row.periodTo} />
                    </span>
                    <Money amountMinor={Number(row.net.amount)} />
                  </Stack>
                }
                secondary={
                  <StatusChip
                    tone={statementTone(row.status)}
                    labelKey={`cmp:statement_status.${row.status}`}
                  />
                }
                slotProps={{ secondary: { component: 'div' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {openId && <PortalStatementDialog id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

/** صورت‌وضعیت خود با پذیرش یا اعتراض به اقلام انتخابی (AC-CMP-043.1). */
function PortalStatementDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation('cmp');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [disputing, setDisputing] = useState(false);
  const [comment, setComment] = useState('');
  const key = [...compensationKeys.portal(session?.tenantId), 'statements', id];
  const statement = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/compensation/me/statements/{id}', { params: { path: { id } } })),
  });
  const after = (data: unknown) => {
    queryClient.setQueryData(key, data);
    void queryClient.invalidateQueries({ queryKey: compensationKeys.portal(session?.tenantId) });
  };
  const acknowledge = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/me/statements/{id}:acknowledge', { params: { path: { id } } }),
      ),
    onSuccess: after,
  });
  const dispute = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/me/statements/{id}:dispute', {
          params: { path: { id } },
          body: { lineIds: selected, comment: comment.trim() },
        }),
      ),
    onSuccess: (data) => {
      after(data);
      setSelected([]);
      setComment('');
      setDisputing(false);
    },
  });
  const view = statement.data;
  const open = view?.summary.status === 'Approved';
  const toggle = (lineId: string) =>
    setSelected((current) =>
      current.includes(lineId) ? current.filter((value) => value !== lineId) : [...current, lineId],
    );

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      aria-labelledby="portal-statement-title"
    >
      <DialogTitle id="portal-statement-title">{t('cmp:statements.detail_title')}</DialogTitle>
      <DialogContent>
        {statement.error ? (
          <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />
        ) : (
          view && (
            <Stack spacing={2}>
              <StatementDetail
                view={view}
                selected={open ? selected : undefined}
                onToggle={open ? toggle : undefined}
              />
              {disputing && (
                <TextField
                  label={t('cmp:portal.dispute_comment')}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  multiline
                  minRows={2}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { 'data-testid': 'portal-dispute-comment', maxLength: 1000 } }}
                />
              )}
              {(acknowledge.error ?? dispute.error) ? (
                <ErrorState error={acknowledge.error ?? dispute.error} compact />
              ) : null}
            </Stack>
          )
        )}
      </DialogContent>
      <DialogActions>
        {open && !disputing && (
          <>
            <Button
              color="warning"
              disabled={selected.length === 0}
              onClick={() => setDisputing(true)}
              data-testid="portal-dispute"
            >
              {t('cmp:portal.dispute', { value: faDigits(selected.length) })}
            </Button>
            <Button
              variant="contained"
              onClick={() => acknowledge.mutate()}
              disabled={acknowledge.isPending}
              data-testid="portal-acknowledge"
            >
              {t('cmp:portal.acknowledge')}
            </Button>
          </>
        )}
        {open && disputing && (
          <Button
            variant="contained"
            color="warning"
            disabled={!comment.trim() || dispute.isPending}
            onClick={() => dispute.mutate()}
            data-testid="portal-dispute-submit"
          >
            {t('cmp:portal.dispute_submit')}
          </Button>
        )}
        <Button onClick={onClose}>{t('cmp:detail.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AppointmentsList() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const appointments = useQuery({
    queryKey: [...compensationKeys.portal(session?.tenantId), 'appointments'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/me/appointments', { params: { query: {} } })),
  });
  if (appointments.isPending) {
    return <LoadingState />;
  }
  if (appointments.error) {
    return <ErrorState error={appointments.error} onRetry={() => void appointments.refetch()} />;
  }
  if (appointments.data.items.length === 0) {
    return <EmptyState titleKey="cmp:portal.no_appointments" />;
  }
  return (
    <List dense aria-label={t('cmp:portal.tab_appointments')} data-testid="portal-appointments">
      {appointments.data.items.map((row) => (
        <ListItem key={row.appointmentId} divider disableGutters sx={{ px: 1 }}>
          <ListItemText
            primary={
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <JalaliDate value={row.start} withTime />
                <span>{row.patientName ?? '—'}</span>
              </Stack>
            }
            secondary={row.services.join(LIST_SEPARATOR)}
          />
        </ListItem>
      ))}
    </List>
  );
}
